import type { AppointmentService } from "./data/mock-appointments";

export interface SharedAppointmentDisplayOption {
  id: string;
  name: string;
  duration: number;
  sharedAppointmentTypeId?: string;
  sharedAppointmentTypeDurationId?: string;
  services: AppointmentService[];
}

export function appointmentServiceOfferingKey(
  service: AppointmentService,
): string {
  return (
    service.offeringKey ??
    [
      service.providerUserId ?? "provider",
      service.scheduleConfigurationId ?? "configuration",
      service.scheduleId ?? "schedule",
      service.id,
    ].join(":")
  );
}

export function appointmentServiceDisplayKey(
  service: AppointmentService,
): string {
  if (
    service.sharedAppointmentTypeId &&
    service.sharedAppointmentTypeDurationId
  ) {
    return [
      "shared-appointment",
      service.sharedAppointmentTypeId,
      service.sharedAppointmentTypeDurationId,
    ].join(":");
  }

  return `appointment:${appointmentServiceOfferingKey(service)}`;
}

/**
 * Display-only grouping. Concrete services retain the keys used for
 * availability, payment and booking. Equal labels never imply membership.
 */
export function groupSharedAppointmentServices(
  services: AppointmentService[],
): SharedAppointmentDisplayOption[] {
  const options = new Map<string, SharedAppointmentDisplayOption>();

  for (const service of services) {
    const id = appointmentServiceDisplayKey(service);
    const existing = options.get(id);
    if (existing) {
      existing.services.push(service);
      continue;
    }

    options.set(id, {
      id,
      name: service.name,
      duration: service.duration,
      sharedAppointmentTypeId: service.sharedAppointmentTypeId,
      sharedAppointmentTypeDurationId: service.sharedAppointmentTypeDurationId,
      services: [service],
    });
  }

  return [...options.values()];
}

export function sharedAppointmentProviderIds(
  option: SharedAppointmentDisplayOption,
): number[] {
  return [
    ...new Set(
      option.services.flatMap((service) =>
        service.providerUserId == null ? [] : [service.providerUserId],
      ),
    ),
  ];
}
